import React, { useMemo } from 'react';

import { ContainerHeader, Spinner } from '~components';
import { faMoneyBill } from '~components/icon';
import TabContainer, { Tab } from '~components/TabContainer';
import { ClientSignupsContainer } from '~direct-debits/components/client-signups';
import { ConnectionManager } from '~direct-debits/components/connection';
import { MandateMatchingContainer } from '~direct-debits/components/mandates';
import { PaymentBatchesContainer } from '~direct-debits/components/payment-batches';
import { useGetConnectionsQuery } from '~direct-debits/actions/connections';

const allTabs: Tab[] = [
  {
    id: 'direct-debits-connection',
    title: 'Connection',
    component: ConnectionManager,
  },
  {
    id: 'direct-debits-client-signups',
    title: 'Client Signups',
    component: ClientSignupsContainer,
  },
  {
    id: 'direct-debits-mandate-matching',
    title: 'Mandate Matching',
    component: MandateMatchingContainer,
  },
  {
    id: 'direct-debits-payment-batches',
    title: 'Payment Batches',
    component: PaymentBatchesContainer,
  },
];

const CONNECTION_TAB_ID = 'direct-debits-connection';

const DirectDebitsContainer: React.FC = () => {
  // The non-Connection tabs depend on a working GoCardless link — without
  // one every endpoint they hit fails with 502/error. Hide them until a
  // connection is active so the user has one obvious next step. RTK Query
  // dedupes with the ConnectionManager's own subscription so this doesn't
  // double-fetch.
  // `isFetching` covers both the initial load and any in-flight refetch
  // (e.g. after a tag-driven invalidation), so the spinner reflects every
  // network round-trip — `isLoading` would only fire on the very first one.
  const { data: connections, isFetching: isConnectionsFetching } = useGetConnectionsQuery();

  const hasActiveConnection = useMemo(
    () => (connections ?? []).some((connection) => connection.status === 'active'),
    [connections],
  );

  const visibleTabs = useMemo(
    () => (hasActiveConnection ? allTabs : allTabs.filter((tab) => tab.id === CONNECTION_TAB_ID)),
    [hasActiveConnection],
  );

  return (
    <div className="p-2">
      <ContainerHeader icon={faMoneyBill} iconSize="1x" className="info" dataCy="direct-debits-header">
        Direct Debits — GoCardless
      </ContainerHeader>
      {isConnectionsFetching ? (
        <div className="my-3" data-testid="direct-debits-connection-loading">
          <Spinner loading />
        </div>
      ) : (
        <TabContainer tabs={visibleTabs} setPageTitle={false} />
      )}
    </div>
  );
};

export default DirectDebitsContainer;
